import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useQueries } from '@tanstack/react-query'
import theme from 'assets/theme'
import { getDistance, getHeadingDate, getPolygonCenter } from 'components/KNMapLibre/helpers'
import KNMapLibre from 'components/KNMapLibre/KNMapLibre'
import {
  type BasicMapMarker,
  hasSequenceGroup,
  type MapMarker,
  MapMarkerState,
  type SequenceGroupMapMarker,
} from 'components/KNMapLibre/types'
import { TripDetailsContext } from 'context/trips/TripDetailsContext'
import { isSameDay, isThisYear, parseISO } from 'date-fns'
import { compactZonedDate, relativeDate, zonedDate } from 'global/helpers/dates'
import { combineQueries } from 'global/helpers/queries'
import i18n from 'i18n'
import { type ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import type { StopsLocationGroup, TripData } from 'screens/TripDashboard/TripDashboard.types'
import {
  getParkingLocationColor,
  getStopsGroupStateColor,
  getTypeIcon,
  groupGeoPointsBySpeed,
  groupStopsBySequence,
  positionDataTransformer,
} from 'screens/TripDetails/TripDetails.helpers'
import {
  tripGeofencesQueryOptions,
  tripShortestRoutesQueryOptions,
  tripVehiclePositionsQueryOptions,
} from 'screens/TripDetails/TripDetails.service'
import type {
  Geofence,
  GeoPoint,
  GeoPointsGroup,
  LegData,
  ParkingLocation,
  StatusTripLogData,
  StopsSequenceGroup,
  TripLogData,
} from 'screens/TripDetails/TripDetails.types'
import type { VehicleData } from 'screens/VehicleManager/VehicleManager.types'

export interface SequenceMapLibreViewProps {
  trip: TripData
  legs: LegData[]
  tripLogs: TripLogData[]
  parkingLocations?: ParkingLocation[]
  weblinkToken?: string
}

const combineMapQueries = combineQueries(['positions', 'shortestRoutes', 'geofences'])

export const getStopsMarkers = (groups: StopsSequenceGroup[], tripLogs: TripLogData[]): SequenceGroupMapMarker[] =>
  groups.reduce((markers: SequenceGroupMapMarker[], group: StopsSequenceGroup) => {
    if (group.stopLegPairs[0]?.stop.geoPoint) {
      markers.push({
        id: group.stopLegPairs[0].stop.wayPointCid,
        latitude: group.stopLegPairs[0].stop.geoPoint.latitude,
        longitude: group.stopLegPairs[0].stop.geoPoint.longitude,
        geofenceRadius: group.stopLegPairs[0].stop.geofenceRadius ?? 500,
        geofencePolygon: group.stopLegPairs[0].stop.polygonRegion,
        label: group.id.toString(),
        color: getStopsGroupStateColor(group.state),
        tooltip: getStopTooltip(group, tripLogs) ?? undefined,
        sequenceGroup: group,
      })
    }
    return markers
  }, [])

export const getStopTooltip = (group: StopsSequenceGroup | StopsLocationGroup, tripLogs: TripLogData[] = []): ReactElement => {
  const relatedTripLogs = tripLogs.filter((tripLog) => tripLog.data
    && 'geoPoint' in tripLog.data
    && group.stopLegPairs[0]?.stop.geoPoint
    && tripLog.data.geoPoint
    && tripLog.data.geoPoint.latitude === group.stopLegPairs[0].stop.geoPoint.latitude
    && tripLog.data.geoPoint.longitude === group.stopLegPairs[0].stop.geoPoint.longitude)
  let pickupsCount = 0
  let deliveriesCount = 0
  let customsCount = 0
  group.stopLegPairs.forEach((pair) => {
    if (pair.stop.type === 'PUP') {
      pickupsCount++
    }
    if (pair.stop.type === 'DEL') {
      deliveriesCount++
    }
    if (pair.stop.type === 'CUS') {
      customsCount++
    }
  })

  const PickupIcon = getTypeIcon('PUP')
  const DeliveryIcon = getTypeIcon('DEL')
  const CustomsIcon = getTypeIcon('CUS')

  return (
    <>
      <Typography component="p" variant="p4" color="primary.main">
        {group.stopLegPairs[0]!.stop.type === 'CUS' ? i18n.t('waypoints.types.CUS') : group.stopLegPairs[0]!.stop.address.name.join(' ')}
      </Typography>
      {pickupsCount > 0 && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <PickupIcon sx={{ width: '.75rem', height: '.75rem', fill: theme.palette.primary.main }} />
          <Typography component="p" variant="p5">
            {i18n.t('screens.cs.trip_details.card.pickups_count', { count: pickupsCount })}
          </Typography>
        </Stack>
      )}
      {deliveriesCount > 0 && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <DeliveryIcon sx={{ width: '.75rem', height: '.75rem', fill: theme.palette.primary.main }} />
          <Typography component="p" variant="p5">
            {i18n.t('screens.cs.trip_details.card.deliveries_count', { count: deliveriesCount })}
          </Typography>
        </Stack>
      )}
      {customsCount > 0 && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <CustomsIcon sx={{ width: '.75rem', height: '.75rem', fill: theme.palette.primary.main }} />
          <Typography component="p" variant="p5">
            {i18n.t('screens.cs.trip_details.card.customs_count', { count: customsCount })}
          </Typography>
        </Stack>
      )}
      {relatedTripLogs.map((tripLog) => (
        <Typography component="p" variant="p5" color="primary.light" key={tripLog.entityId}>
          {i18n.t(`screens.cs.trip_details.events_tooltip.${tripLog.type}`, {
            date: compactZonedDate(tripLog.createdAt),
            shipmentNumber: getShipmentId(tripLog),
          })}
        </Typography>
      ))}
    </>
  )
}
const getShipmentId = (tripLog: TripLogData): string => (tripLog.data as StatusTripLogData).shipmentNumber

export const getFirstPositionMarker = (geoPoint: GeoPoint): BasicMapMarker => {
  return {
    id: 'first_position',
    latitude: geoPoint.latitude,
    longitude: geoPoint.longitude,
    type: 'FIRST_POSITION',
    tooltip: (
      <>
        <Typography variant="p3">{relativeDate(geoPoint.timestamp!)}</Typography>
        <Typography component="p" variant="p5">{getHeadingDate(geoPoint.timestamp!)}</Typography>
        <Typography component="p" variant="p5" color="primary.light">
          {i18n.t('screens.cs.trip_details.map.tracking_started')}
        </Typography>
      </>
    ),
  }
}

export const getVehicleMarker = (vehicle: VehicleData, lastVehiclePosition: GeoPoint): BasicMapMarker => {
  return {
    id: vehicle.licensePlate,
    latitude: lastVehiclePosition.latitude,
    longitude: lastVehiclePosition.longitude,
    type: 'VEHICLE',
    tooltip: (
      <>
        <Typography variant="p4" color="primary.main">{vehicle.licensePlate}</Typography>
        <Typography component="p" variant="p5">
          {relativeDate(lastVehiclePosition.lastTimestamp ?? lastVehiclePosition.timestamp!)}
        </Typography>
        <Typography component="p" variant="p5">
          {getHeadingDate(lastVehiclePosition.timestamp!, lastVehiclePosition.lastTimestamp)}
        </Typography>
      </>
    ),
  }
}

export const getGeofenceMarkers = (geofences: Geofence[], tripLogs: TripLogData[]): BasicMapMarker[] =>
  // ignore waypoint geofences, those are set already on waypoint markers
  // only circular geofences are supported for now
  geofences
    .filter((geofence) => geofence.type !== 'WAYPOINT_MILESTONE region' && geofence.geometry.type === 'POINT')
    .map((geofence) => {
      return {
        id: geofence.id,
        latitude: geofence.geometry.coordinates[0]!.lat,
        longitude: geofence.geometry.coordinates[0]!.lon,
        type: 'GEOFENCE',
        geofenceRadius: geofence.geometry.radius ?? 500,
        tooltip: getGeofenceTooltip(geofence, tripLogs) ?? undefined,
      }
    })

export const getGeofenceTooltip = (geofence: Geofence, tripLogs: TripLogData[]): ReactElement | null => {
  const relatedTripLogs = tripLogs.filter((tripLog) => tripLog.data
    && 'geoPoint' in tripLog.data
    && tripLog.data.geoPoint
    && tripLog.data.geoPoint.latitude === geofence.geometry.coordinates[0]?.lat
    && tripLog.data.geoPoint.longitude === geofence.geometry.coordinates[0]?.lon)
  return (
    <>
      <Typography variant="p4" color="primary.main">{geofence.name}</Typography>
      {relatedTripLogs.map((tripLog) => (
        <Typography component="p" variant="p5" color="primary.light" key={tripLog.entityId}>
          {i18n.t(`screens.cs.trip_details.events_tooltip.${tripLog.type}`, { date: compactZonedDate(tripLog.createdAt) })}
        </Typography>
      ))}
    </>
  )
}

export const getParkingLocationMarkers = (parkingLocations?: ParkingLocation[]): BasicMapMarker[] =>
  parkingLocations?.map((parkingLocation) => {
    const parkingLocationMarker: BasicMapMarker = {
      id: parkingLocation.entityId,
      latitude: parkingLocation.geometry.coordinates[0]!.latitude,
      longitude: parkingLocation.geometry.coordinates[0]!.longitude,
      color: getParkingLocationColor(parkingLocation.type),
      type: 'PARKING_LOCATION',
      subType: parkingLocation.type as string,
      tooltip: getParkingLocationTooltip(parkingLocation),
      compactTooltip: getCompactParkingLocationTooltip(parkingLocation),
      groupable: true,
    }
    // draw geofence when it's a polygon and calculate it's actual center
    if (parkingLocation.geometry.type === 'POLYGON') {
      const polygonCenter = getPolygonCenter(parkingLocation.geometry.coordinates)
      parkingLocationMarker.latitude = polygonCenter.latitude
      parkingLocationMarker.longitude = polygonCenter.longitude
      parkingLocationMarker.geofencePolygon = parkingLocation.geometry.coordinates
    }
    return parkingLocationMarker
  }) ?? []

export const getParkingLocationTooltip = (parkingLocation: ParkingLocation): ReactElement => (
  <>
    <Typography variant="p4" color="primary.main">
      {parkingLocation.properties.title}
    </Typography>
    <Typography component="p" variant="p5" color={getParkingLocationColor(parkingLocation.type)}>
      {i18n.t(`screens.cs.trip_details.parking_locations.${parkingLocation.type}`)}
    </Typography>
  </>
)

export const getCompactParkingLocationTooltip = (parkingLocation: ParkingLocation): ReactElement => (
  <Typography component="p" variant="p5" color={getParkingLocationColor(parkingLocation.type)}>
    {i18n.t(`screens.cs.trip_details.parking_locations.${parkingLocation.type}`)}
  </Typography>
)

export const getDwellLocations = (geoPoints: GeoPoint[]): GeoPoint[][] => {
  const distanceThershold = 1 // kilometers
  const timeThreshold = 30 * 60 // seconds

  const dwellLocations: GeoPoint[][] = []
  let currentDwell: GeoPoint[] = []
  let elapsedTime = 0
  geoPoints.forEach((geoPoint) => {
    if (currentDwell.length === 0) {
      currentDwell.push(geoPoint)
      return
    }
    if (getDistance(geoPoint, currentDwell[0]!) <= distanceThershold) {
      currentDwell.push(geoPoint)
      elapsedTime += geoPoint.timeToPrevious ?? 0
    } else {
      if (elapsedTime >= timeThreshold) {
        dwellLocations.push(currentDwell)
      }
      currentDwell = [geoPoint]
      elapsedTime = 0
    }
  })
  if (elapsedTime >= timeThreshold) {
    dwellLocations.push(currentDwell)
  }
  return dwellLocations
}

export const getDwellMarkers = (dwellLocations: GeoPoint[][]): BasicMapMarker[] =>
  dwellLocations.map((dwell) => {
    return {
      id: `dwell_${dwell[0]!.latitude}_${dwell[0]!.longitude}`,
      latitude: dwell[0]!.latitude,
      longitude: dwell[0]!.longitude,
      type: 'DWELL_TIME',
      geofenceRadius: 1000,
      tooltip: getDwellTooltip(dwell),
      compactTooltip: getCompactDwellTooltip(dwell),
      groupable: true,
    }
  })

export const getDwellTooltip = (dwell: GeoPoint[]): ReactElement => {
  const first = dwell[0]
  const last = dwell[dwell.length - 1]
  const parsedFirstTimestamp = parseISO(first!.timestamp!)
  const parsedLastTimestamp = parseISO(last!.timestamp!)
  const fullDateFormat = isThisYear(parsedFirstTimestamp) ? 'full_no_year' : 'full'
  const mediumDateFormat = isThisYear(parsedFirstTimestamp) ? 'medium_no_year' : 'medium'
  const difference = relativeDate(parseISO(last!.timestamp!), parseISO(first!.timestamp!))
  return (
    <>
      <Typography variant="p3">{i18n.t('screens.cs.trip_details.map.dwell_time', { difference })}</Typography>
      {isSameDay(parsedFirstTimestamp, parsedLastTimestamp) ? (
        <Typography component="p" variant="p5">
          {`${zonedDate(parsedFirstTimestamp, mediumDateFormat)!} ${zonedDate(parsedFirstTimestamp, 'time')!}-${zonedDate(parsedLastTimestamp, 'time')!}`}
        </Typography>
      ) : (
        <Typography component="p" variant="p5">
          {`${zonedDate(parsedFirstTimestamp, fullDateFormat)!} - ${zonedDate(parsedLastTimestamp, fullDateFormat)!}`}
        </Typography>
      )}
    </>
  )
}

export const getCompactDwellTooltip = (dwell: GeoPoint[]): ReactElement => {
  const first = dwell[0]
  const last = dwell[dwell.length - 1]
  const parsedFirstTimestamp = parseISO(first!.timestamp!)
  const parsedLastTimestamp = parseISO(last!.timestamp!)
  const fullDateFormat = isThisYear(parsedFirstTimestamp) ? 'full_no_year' : 'full'
  const mediumDateFormat = isThisYear(parsedFirstTimestamp) ? 'medium_no_year' : 'medium'
  return isSameDay(parsedFirstTimestamp, parsedLastTimestamp) ? (
    <Typography component="p" variant="p5">
      {`${zonedDate(parsedFirstTimestamp, mediumDateFormat)!} ${zonedDate(parsedFirstTimestamp, 'time')!}-${zonedDate(parsedLastTimestamp, 'time')!}`}
    </Typography>
  ) : (
    <Typography component="p" variant="p5">
      {`${zonedDate(parsedFirstTimestamp, fullDateFormat)!} - ${zonedDate(parsedLastTimestamp, fullDateFormat)!}`}
    </Typography>
  )
}

const SequenceMapLibreView = ({
  trip,
  legs,
  tripLogs,
  parkingLocations,
  weblinkToken,
}: SequenceMapLibreViewProps): ReactElement => {
  const [tripDetailsState, tripDetailsDispatch] = useContext(TripDetailsContext)
  const mapQueries = useQueries({
    queries: [
      tripVehiclePositionsQueryOptions(trip.entityId, weblinkToken),
      tripShortestRoutesQueryOptions(trip.entityId, weblinkToken),
      tripGeofencesQueryOptions(trip.entityId, weblinkToken),
    ],
    combine: combineMapQueries,
  })
  // markers generated from props data
  const [staticMarkers, setStaticMarkers] = useState<MapMarker[]>([])
  // markers generated from remote calls
  const [dynamicMarkers, setDynamicMarkers] = useState<MapMarker[]>([])
  // all markers combined
  const [markers, setMarkers] = useState<MapMarker[]>([])
  const [geoPoints, setGeoPoints] = useState<GeoPoint[]>([])
  const [groupedGeoPoints, setGroupedGeoPoints] = useState<GeoPointsGroup[]>([])
  const [shortestRoutesGeoPoints, setShortestRoutesGeoPoints] = useState<GeoPoint[][]>([])

  useEffect(() => {
    const groupedStops = groupStopsBySequence(legs)
    setStaticMarkers([
      ...getStopsMarkers(groupedStops, tripLogs),
      ...getParkingLocationMarkers(parkingLocations),
    ])
  }, [legs, parkingLocations])

  useEffect(() => {
    if (!mapQueries.isSuccess) {
      return
    }
    const vehiclePositions = positionDataTransformer(mapQueries.data.positions)
    setGeoPoints(vehiclePositions)
    setGroupedGeoPoints(groupGeoPointsBySpeed(vehiclePositions))
    setShortestRoutesGeoPoints(mapQueries.data.shortestRoutes.map((route) => route.coordinates))
    if (vehiclePositions.length === 0) {
      setDynamicMarkers([])
      return
    }
    const dwellLocations = getDwellLocations(vehiclePositions)
    const dynamicMarkers: MapMarker[] = [
      getFirstPositionMarker(vehiclePositions[0]!),
      ...getGeofenceMarkers(mapQueries.data.geofences, tripLogs),
      ...getDwellMarkers(dwellLocations),
    ]
    const lastVehiclePosition = vehiclePositions[vehiclePositions.length - 1]!
    if (trip.assignedTrailer) {
      dynamicMarkers.push(getVehicleMarker(trip.assignedTrailer, lastVehiclePosition))
    } else if (trip.assignedTruck) {
      dynamicMarkers.push(getVehicleMarker(trip.assignedTruck, lastVehiclePosition))
    }
    setDynamicMarkers(dynamicMarkers)
  }, [mapQueries, trip, tripLogs])

  useEffect(() => {
    setMarkers([
      ...staticMarkers,
      ...dynamicMarkers,
    ])
  }, [staticMarkers, dynamicMarkers])

  useEffect(() => {
    setMarkers(markers.map((marker) => {
      marker.state = MapMarkerState.Default
      if (hasSequenceGroup(marker)) {
        marker.state = tripDetailsState.activeStopsGroup === marker.sequenceGroup.id ? MapMarkerState.Active : MapMarkerState.Default
      }
      return marker
    }))
  }, [tripDetailsState.activeStopsGroup])

  const handleMarkerClick = useCallback((marker: MapMarker) => {
    if (hasSequenceGroup(marker)) {
      tripDetailsDispatch({
        type: 'setActiveStopsGroup',
        payload: marker.sequenceGroup.id,
      })
      tripDetailsDispatch({
        type: 'setDetailsView',
        payload: 'stops',
      })
    }
    if (marker.type === 'PARKING_LOCATION') {
      const parkingLocation = parkingLocations?.find(parkingLocation => parkingLocation.entityId === marker.id)
      if (parkingLocation) {
        tripDetailsDispatch({
          type: 'setParkingLocationFocus',
          payload: parkingLocation,
        })
      }
    }
  }, [])

  return (
    <Paper
      sx={{
        flexGrow: 1,
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
    >
      {mapQueries.isPending ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <CircularProgress size={64} color="primary" />
        </Stack>
      ) : (
        <KNMapLibre
          markers={markers}
          geoPoints={geoPoints}
          groupedGeoPoints={groupedGeoPoints}
          withHeading
          onMarkerClick={handleMarkerClick}
          shortestRoutes={shortestRoutesGeoPoints}
          focusGeoPoint={tripDetailsState.focusGeoPoint}
        />
      )}
    </Paper>
  )
}

export default SequenceMapLibreView
